import React, { useMemo } from 'react';
import { AlertTriangle, CheckCircle2, HeartPulse, Loader2, ShieldAlert, Wrench } from 'lucide-react';
import {
  type FixHistoryEvent,
  type PlatformHealthFailure,
  useDismissAutoHealIssue,
  useFixHistory,
  usePlatformHealth,
  useTriggerAutoHealFix,
} from '../hooks/usePlatformHealth';
import { useHealthSummary } from '../hooks';
import {
  derivePlatformHealthHonesty,
  honestyLabel,
  honestyTone,
  type IssueHonesty,
} from '../../../lib/failureHonesty';

const PANEL_CLASS = 'rounded-2xl bg-white p-5';
const PANEL_STYLE = { boxShadow: 'var(--shadow-card)' } as const;

function formatRelativeTime(value?: string | null) {
  if (!value) return 'unknown';
  const diffMs = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(diffMs) || diffMs <= 0) return 'just now';
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function scoreTone(score: number) {
  if (score >= 85) return 'text-emerald-700 bg-emerald-50 border-emerald-200';
  if (score >= 60) return 'text-amber-700 bg-amber-50 border-amber-200';
  return 'text-red-700 bg-red-50 border-red-200';
}

function trendLabel(trend?: string) {
  if (trend === 'up') return 'Failure rate rising';
  if (trend === 'down') return 'Failure rate improving';
  if (trend === 'stable') return 'Failure rate stable';
  return 'Trend unavailable';
}

function statusLabel(status: string, classification?: string, honesty?: IssueHonesty) {
  if (honesty) return honestyLabel(honesty, status, classification);
  return honestyLabel(
    classification === 'caller_error' || status === 'skipped' ? 'caller_error' : 'open',
    status,
    classification,
  );
}

function diagnosisSummary(item: PlatformHealthFailure | FixHistoryEvent) {
  const diagnosis = item.diagnosis as Record<string, unknown> | null | undefined;
  if (!diagnosis) return 'Awaiting classification';
  if (typeof diagnosis.primary_error === 'string') return diagnosis.primary_error;
  if (typeof diagnosis.issue === 'string') return diagnosis.issue;
  if (typeof diagnosis.classification === 'string') return `${diagnosis.classification} issue`;
  return 'Diagnosis recorded';
}

function ActivityRow({
  item,
  honesty,
  onApplyFix,
  onDismiss,
  applying,
  dismissing,
}: {
  item: PlatformHealthFailure | FixHistoryEvent;
  honesty: IssueHonesty;
  onApplyFix?: () => void;
  onDismiss?: () => void;
  applying?: boolean;
  dismissing?: boolean;
}) {
  const tone = honestyTone(honesty);
  const isResolved = tone === 'success';
  const isSkipped = honesty === 'caller_error';
  const Icon = isResolved ? CheckCircle2 : isSkipped ? ShieldAlert : AlertTriangle;
  const iconClass = tone === 'success'
    ? 'text-emerald-600'
    : tone === 'warning'
      ? 'text-slate-500'
      : 'text-red-600';

  const canApply = Boolean(
    onApplyFix
    && honesty === 'open'
    && ['diagnosed', 'active', 'fix_failed', 'rate_limited', 'pending_diagnosis'].includes(item.status),
  );
  const canDismiss = Boolean(onDismiss && 'id' in item && item.id);

  return (
    <div className="rounded-xl border border-slate-100 px-3 py-3">
      <div className="flex items-start gap-3">
        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${iconClass}`} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium text-slate-900">{item.tool_name}</p>
            {'failure_count' in item && item.failure_count != null && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
                {item.failure_count} failures
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-slate-600">{diagnosisSummary(item)}</p>
          <p className="mt-1 text-[11px] text-slate-500">
            {statusLabel(item.status, item.classification || undefined, honesty)}
            {' · '}
            {formatRelativeTime('last_failure' in item ? item.last_failure : item.event_time)}
          </p>
          {(canApply || canDismiss) && (
            <div className="mt-3 flex flex-wrap gap-2">
              {canApply && (
                <button
                  type="button"
                  onClick={onApplyFix}
                  disabled={applying}
                  className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-2.5 py-1.5 text-[11px] font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {applying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wrench className="h-3 w-3" />}
                  Apply fix
                </button>
              )}
              {canDismiss && (
                <button
                  type="button"
                  onClick={onDismiss}
                  disabled={dismissing}
                  className="inline-flex items-center rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                >
                  Dismiss
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PlatformHealthCard({ enabled = true }: { enabled?: boolean }) {
  const healthQ = usePlatformHealth(enabled);
  const serviceHealthQ = useHealthSummary(enabled);
  const historyQ = useFixHistory({ enabled, limit: 8, hours: 24 });
  const triggerFix = useTriggerAutoHealFix();
  const dismissIssue = useDismissAutoHealIssue();

  const honesty = useMemo(() => derivePlatformHealthHonesty({
    activeFailures: healthQ.data?.active_failures || [],
    history: historyQ.data?.events || [],
    backendScore: healthQ.data?.health_score ?? 100,
    serviceHealth: serviceHealthQ.data,
  }), [healthQ.data, historyQ.data?.events, serviceHealthQ.data]);

  const downServices = serviceHealthQ.data?.down ?? 0;
  const degradedServices = serviceHealthQ.data?.degraded ?? 0;
  const platformIssues = honesty.activeIssueCount;
  const callerIssues = honesty.callerIssueCount;
  const score = honesty.score;

  const recentActivity = useMemo(
    () => honesty.cards.slice(0, 5),
    [honesty.cards],
  );

  const knownTools = healthQ.data?.total_tools
    ?? healthQ.data?.active_tools
    ?? healthQ.data?.healthy_tools
    ?? 0;

  if (!enabled) return null;

  return (
    <section className={PANEL_CLASS} style={PANEL_STYLE}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <HeartPulse className="h-4 w-4 text-red-500" />
            <h2 className="text-base font-semibold text-slate-900">Platform Health & Fixes</h2>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            MCP failure watcher, diagnoses, and fix attempts from Timescale telemetry.
          </p>
        </div>
        <div className={`rounded-xl border px-3 py-2 text-sm font-semibold ${scoreTone(score)}`}>
          Score: {score}/100
        </div>
      </div>

      {(healthQ.isLoading || historyQ.isLoading) && (
        <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-6 text-sm text-slate-500">
          Loading platform health...
        </div>
      )}

      {!healthQ.isLoading && healthQ.isError && (
        <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-4 text-sm text-red-700">
          Platform health unavailable. Auto-heal routes may not be deployed yet.
        </div>
      )}

      {!healthQ.isLoading && !healthQ.isError && healthQ.data && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Active issues</p>
              <p className="text-lg font-semibold text-slate-900">{platformIssues}</p>
              {honesty.openIssueCount > 0 && (
                <p className="mt-1 text-[11px] text-slate-500">
                  {honesty.openIssueCount} open failure card{honesty.openIssueCount === 1 ? '' : 's'}
                </p>
              )}
              {downServices > 0 && (
                <p className="mt-1 text-[11px] text-slate-500">
                  {downServices} service{downServices === 1 ? '' : 's'} down
                </p>
              )}
              {degradedServices > 0 && (
                <p className="mt-1 text-[11px] text-slate-500">
                  {degradedServices} service{degradedServices === 1 ? '' : 's'} degraded
                </p>
              )}
              {callerIssues > 0 && (
                <p className="mt-1 text-[11px] text-slate-500">
                  {callerIssues} caller arg error(s) — not platform failures
                </p>
              )}
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Fixes in progress</p>
              <p className="text-lg font-semibold text-slate-900">{healthQ.data.fixes_in_progress}</p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Resolved (24h)</p>
              <p className="text-lg font-semibold text-slate-900">{healthQ.data.resolved_24h}</p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Tools with recent success</p>
              <p className="text-lg font-semibold text-slate-900">{healthQ.data.healthy_tools}</p>
              <p className="mt-1 text-[11px] text-slate-500">
                {healthQ.data.healthy_tools} of {knownTools} known MCP tools in the watch window — coverage, not the platform score
              </p>
              {(healthQ.data.inactive_tools ?? 0) > 0 && (
                <p className="mt-1 text-[11px] text-slate-500">
                  {healthQ.data.inactive_tools} inactive (no calls in window)
                </p>
              )}
            </div>
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-3 text-xs text-slate-500">
            <span className={`inline-flex items-center gap-1 ${healthQ.data.watcher_running ? 'text-emerald-700' : 'text-amber-700'}`}>
              <span className={`h-2 w-2 rounded-full ${healthQ.data.watcher_running ? 'bg-emerald-500' : 'bg-amber-500'}`} />
              Watcher {healthQ.data.watcher_running ? 'running' : 'stopped'}
            </span>
            <span>{trendLabel(healthQ.data.failure_trend)}</span>
            {healthQ.data.last_poll && <span>Last poll {formatRelativeTime(healthQ.data.last_poll)}</span>}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-medium text-slate-900">Recent Activity</h3>
            </div>

            {recentActivity.length === 0 && (
              <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                No MCP failures above threshold in the current window.
              </div>
            )}

            {recentActivity.map(({ item, honesty: cardHonesty }) => {
              const key = `${item.tool_name}-${item.app_id}-${'id' in item ? item.id : ('last_failure' in item ? item.last_failure : 'active')}`;
              const fixEventId = 'fix_event_id' in item ? item.fix_event_id : ('id' in item ? item.id : null);
              const isActiveFailure = 'last_failure' in item;

              return (
                <ActivityRow
                  key={key}
                  item={item as PlatformHealthFailure | FixHistoryEvent}
                  honesty={cardHonesty}
                  applying={triggerFix.isPending}
                  dismissing={dismissIssue.isPending}
                  onApplyFix={
                    isActiveFailure || fixEventId
                      ? () => triggerFix.mutate({
                          tool_name: item.tool_name,
                          app_id: item.app_id,
                          auto_fix: true,
                          error_message: diagnosisSummary(item as PlatformHealthFailure | FixHistoryEvent),
                        })
                      : undefined
                  }
                  onDismiss={
                    typeof fixEventId === 'number'
                      ? () => dismissIssue.mutate(fixEventId)
                      : undefined
                  }
                />
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}
